import { error } from '@sveltejs/kit';
import { invalidateAll } from '$app/navigation';
import { authorityGeneration } from './authority-cache';
import { apiClientQuiet as apiClient, onForbiddenResponse } from '$lib/api/client';
import type { ApiIdentity } from '$lib/api/types';
import { ApiError } from '$lib/api/types';
import { identity } from './identity.svelte';

// One memoized /api/v1/me read shared by the root layout, admin gate and
// member-only route loaders. The class is presentation input only: every API
// request is still authorized by current server authority. A server 403 or
// session change calls refreshCurrentIdentity() so a downgrade cannot keep
// rendering a cached member surface past the next refresh.

let identityPromise: Promise<ApiIdentity | null> | null = null;
let lastFetchMs = 0;
let identityEstablished = false;
const IDENTITY_TTL_MS = 60_000;

function adopt(me: ApiIdentity | null): void {
  const subsequent = identityEstablished;
  identityEstablished = true;
  const before = authorityGeneration();
  identity.set(me);
  lastFetchMs = Date.now();
  // A post-initial authority transition (class, session, roles) already
  // cleared every payload cache; also re-run every mounted route loader so
  // the current page re-evaluates its gates (requireMember, admin layout,
  // viewer caps) against the new identity instead of keeping a loaded member
  // surface on screen. Not awaited: the loaders await this same memo promise,
  // which settles as soon as this callback returns.
  if (subsequent && authorityGeneration() !== before) void invalidateAll();
}

function fetchIdentity(): Promise<ApiIdentity | null> {
  // Share a pending root read with child gates instead of treating its unset
  // settlement timestamp as an expired identity and starting another read.
  lastFetchMs = Date.now();
  const p: Promise<ApiIdentity | null> = apiClient
    .me()
    .then((me) => {
      if (identityPromise !== p) return identityPromise;
      adopt(me);
      return me;
    })
    .catch((err) => {
      if (err instanceof ApiError && err.status === 401) {
        if (identityPromise !== p) return identityPromise;
        adopt(null);
        return null;
      }
      // Network errors etc — clear the memo so the next navigation retries.
      if (identityPromise === p) identityPromise = null;
      throw err;
    });
  return p;
}

export function currentIdentity(): Promise<ApiIdentity | null> {
  if (!identityPromise || Date.now() - lastFetchMs > IDENTITY_TTL_MS) {
    identityPromise = fetchIdentity();
  }
  return identityPromise;
}

/** Drop the memo and re-read current authority now. */
export function refreshCurrentIdentity(): Promise<ApiIdentity | null> {
  identityPromise = fetchIdentity();
  return identityPromise;
}

// Any 403 may mean the caller was downgraded since the memoized /me. Re-read
// current authority at most once per window; a changed class/session then
// clears every cached payload through the authority partition. The throttle
// keeps a page whose requests are legitimately forbidden from looping.
const FORBIDDEN_REFRESH_MS = 10_000;
let lastForbiddenRefresh = 0;
onForbiddenResponse(() => {
  const now = Date.now();
  if (now - lastForbiddenRefresh < FORBIDDEN_REFRESH_MS) return;
  lastForbiddenRefresh = now;
  void refreshCurrentIdentity().catch(() => {});
});

export const GUEST_DENIED_MESSAGE =
  'Guest accounts can open only apps explicitly shared with them. This page is not available.';

/**
 * Loader gate for member-only surfaces (directory, groups, tokens, MCP,
 * namespaces, admin). Resolves before the route issues any of its own API
 * calls so a guest deep link settles on one denial instead of a 403 loop.
 */
export async function requireMember(): Promise<ApiIdentity> {
  const me = await currentIdentity();
  // 401 already redirected to login inside the API client.
  if (!me) throw error(401, 'sign in required');
  if (me.user_class !== 'member') throw error(403, GUEST_DENIED_MESSAGE);
  return me;
}
